/*
  Mejoras de accesibilidad (WCAG 2.2 AA) que no se pueden expresar en Liquid
  porque el markup lo genera el bundle del tema (main.mjs).

  Semántica de diálogo para <x-modal> y sus derivados (cart-modal, search-modal,
  quick-add, size chart, ...): el componente ya implementa focus trap, cierre con
  Escape, bloqueo de scroll y foco inicial, pero el contenedor no se anuncia como
  diálogo (WCAG 4.1.2 Name, Role, Value). Aquí se le añaden role="dialog" y
  aria-modal="true", y se le da nombre accesible a partir de su encabezado.

  Deliberadamente NO se marca como diálogo aquello a lo que no se le puede dar un
  nombre accesible: un role="dialog" sin nombre es peor que no tenerlo.
*/
(function () {
  'use strict';

  var uid = 0;

  // Todos los derivados de <x-modal> llevan la clase `modal` y exponen
  // `.content` (el elemento con slot="content"): cart-modal, search-modal,
  // mobile-menu, mega-menu-drawer, facets-drawer, quick-add, size chart...
  function isModalHost(el) {
    return (
      el instanceof Element &&
      el.content instanceof Element &&
      (el.classList.contains('modal') || /(^|-)MODAL$/.test(el.tagName))
    );
  }

  function applyDialogSemantics(host) {
    var content = host.content;
    if (!content) return;

    // El nombre se recalcula en cada apertura: el contenido del carrito se
    // re-renderiza con la Section Rendering API y pierde los ids anteriores.
    var label = host.getAttribute('modal-label');
    var heading = content.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]');

    if (label) {
      content.setAttribute('aria-label', label);
      content.removeAttribute('aria-labelledby');
    } else if (heading && heading.textContent.trim()) {
      if (!heading.id) heading.id = 'a11y-modal-heading-' + ++uid;
      content.setAttribute('aria-labelledby', heading.id);
      content.removeAttribute('aria-label');
    } else if (
      !content.hasAttribute('aria-label') &&
      !content.hasAttribute('aria-labelledby')
    ) {
      // Sin nombre posible: se deja el markup como estaba.
      return;
    }

    content.setAttribute('role', 'dialog');
    content.setAttribute('aria-modal', 'true');
  }

  // `x-modal` emite CustomEvent("show") sin bubbles, pero la fase de captura
  // recorre el árbol igualmente, así que un único listener en document basta.
  document.addEventListener(
    'show',
    function (event) {
      if (!isModalHost(event.target)) return;
      try {
        applyDialogSemantics(event.target);
      } catch (e) {
        /* nunca romper la apertura del modal por un fallo de etiquetado */
      }
    },
    true
  );
})();

/*
  WCAG 2.2.2 Pause, Stop, Hide: [data-autoplay-toggle] buttons for
  <announcement-bar> and <slideshow-element>, driven through their own API.
*/
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setState(btn, paused) {
    btn.toggleAttribute('data-paused', paused);
    btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
  }

  function isAnnouncement(host) {
    return host.tagName === 'ANNOUNCEMENT-BAR';
  }

  function pauseHost(host) {
    if (isAnnouncement(host)) {
      host.stopAutoplay();
    } else {
      // Its own observer resumes on scroll-back; detach it while user-paused
      if (host.pauseObserver) host.pauseObserver.disconnect();
      host.pause();
    }
  }

  function playHost(host) {
    if (isAnnouncement(host)) {
      host.autoplay = true;
      host.startAutoplay();
    } else {
      host.play();
      if (host.pauseObserver) host.pauseObserver.observe(host);
    }
  }

  function init(btn) {
    var host = btn.closest('announcement-bar, slideshow-element');
    if (!host) return;

    if (!isAnnouncement(host) && reduceMotion) {
      // The slideshow already skips autoplay under reduced motion
      btn.hidden = true;
      return;
    }

    if (isAnnouncement(host)) {
      if (reduceMotion) {
        host.stopAutoplay();
        setState(btn, true);
      }
      // Prev/next already stop the rotation; reflect it
      host.querySelectorAll('[data-button-prev], [data-button-next]').forEach(function (arrow) {
        arrow.addEventListener('click', function () {
          setState(btn, true);
        });
      });
    }

    btn.addEventListener('click', function () {
      var pause = !btn.hasAttribute('data-paused');
      try {
        if (pause) pauseHost(host);
        else playHost(host);
      } catch (e) {
        return;
      }
      setState(btn, pause);
    });
  }

  Promise.all([
    customElements.whenDefined('announcement-bar'),
    customElements.whenDefined('slideshow-element'),
  ]).then(function () {
    document.querySelectorAll('[data-autoplay-toggle]').forEach(init);
    // Theme editor re-renders sections
    document.addEventListener('shopify:section:load', function (event) {
      event.target.querySelectorAll('[data-autoplay-toggle]').forEach(init);
    });
  });
})();
